import React, { useState, useEffect } from "react";
import { CheckCircle2, AlertCircle, Sparkles, Plus, Minus } from "lucide-react";

export interface LastScannedItem {
  code: string;
  timestamp: number;
  success?: boolean;
  message?: string;
  quantity?: number;
}

interface ScannedItemBannerProps {
  lastScanned: LastScannedItem | null;
  onIncrement?: (code: string, newQty: number) => void;
  onDecrement?: (code: string, newQty: number) => void;
}

export const ScannedItemBanner: React.FC<ScannedItemBannerProps> = ({
  lastScanned,
  onIncrement,
  onDecrement,
}) => {
  const [qty, setQty] = useState<number>(1);

  useEffect(() => {
    if (lastScanned?.code) {
      setQty(lastScanned.quantity || 1);
    }
  }, [lastScanned?.code, lastScanned?.timestamp, lastScanned?.quantity]);

  if (!lastScanned) return null;

  const handleMinus = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (qty <= 1) return;
    const nextQty = qty - 1;
    setQty(nextQty);
    onDecrement?.(lastScanned.code, nextQty);
  };

  const handlePlus = (e: React.MouseEvent) => {
    e.stopPropagation();
    const nextQty = qty + 1;
    setQty(nextQty);
    onIncrement?.(lastScanned.code, nextQty);
  };

  return (
    <div className="absolute bottom-5 left-4 right-4 z-50">
      <div
        className={`p-3.5 rounded-2xl border flex items-center justify-between gap-3 text-xs transition-all animate-in fade-in slide-in-from-bottom-4 duration-300 shadow-xl backdrop-blur-md ${
          lastScanned.success === true
            ? "bg-white/95 border-emerald-300 text-emerald-900"
            : lastScanned.success === false
            ? "bg-white/95 border-rose-300 text-rose-900"
            : "bg-white/95 border-indigo-300 text-indigo-900"
        }`}
      >
        <div className="flex items-center gap-3 min-w-0">
          {lastScanned.success === true ? (
            <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
          ) : lastScanned.success === false ? (
            <AlertCircle className="w-6 h-6 text-rose-600 flex-shrink-0" />
          ) : (
            <Sparkles className="w-6 h-6 text-indigo-600 flex-shrink-0 animate-spin" />
          )}
          <div className="truncate">
            <p className="font-bold text-[11px] uppercase tracking-wider text-slate-500">
              {lastScanned.message || "Scanned Barcode"}
            </p>
            <p className="font-mono text-sm sm:text-base font-extrabold text-slate-800 truncate">
              {lastScanned.code}
            </p>
          </div>
        </div>

        {/* Quantity Controls (- Qty +) */}
        {lastScanned.success === true && (
          <div className="flex items-center gap-1.5 bg-slate-100/90 p-1 rounded-xl border border-slate-200 shadow-xs flex-shrink-0">
            <button
              type="button"
              onClick={handleMinus}
              disabled={qty <= 1}
              className="w-7 h-7 rounded-lg bg-white hover:bg-slate-50 disabled:opacity-30 disabled:hover:bg-white text-slate-700 font-bold flex items-center justify-center border border-slate-200/80 shadow-xs transition-all active:scale-95 cursor-pointer"
              title="Decrease quantity"
            >
              <Minus className="w-3.5 h-3.5" />
            </button>

            <span className="px-2 font-mono font-extrabold text-xs text-slate-800 min-w-[24px] text-center select-none">
              {qty}
            </span>

            <button
              type="button"
              onClick={handlePlus}
              className="w-7 h-7 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex items-center justify-center border border-emerald-600 shadow-xs transition-all active:scale-95 cursor-pointer"
              title="Increase quantity"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
